import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, Pause, Play, KeyRound, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

// External registration sync ("shadow mode") — Registrations ▸ External sync.
// Shows each linked external form → its OL shadow form, counts, diffs, the last
// runs, Pause/Resume, and issues a new sync key with the "Sync now" bookmark.
// Backend: /api/ext-reg-sync (routes/ext_reg_sync.py).
const API = `${process.env.REACT_APP_BACKEND_URL}/api/ext-reg-sync`;

function Bookmark({ href }) {
  const ref = useRef(null);
  // React blocks javascript: URLs in JSX; set it on the element directly.
  useEffect(() => { if (ref.current) ref.current.setAttribute('href', href); }, [href]);
  return <a ref={ref} className="inline-block px-3 py-1 rounded bg-amber-600 text-white text-xs font-semibold" data-testid="ext-bookmarklet" onClick={e => e.preventDefault()}>OL Sync now</a>;
}

export default function ExtSyncCard({ orgId, isDark = true, onOpenForm }) {
  const [rows, setRows] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [diffs, setDiffs] = useState(null);
  const [setup, setSetup] = useState(null);   // {label, api_base, ext_org_id, forms_text}
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/${orgId}/config`); setRows(data.configs || []); } catch { setRows([]); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  if (rows === null) return null;

  const save = async () => {
    const forms = (setup.forms_text || '').split(/[\s,;]+/).filter(Boolean).map(x => { const [p, f] = x.split('/'); return { ext_program_id: p, ext_form_id: f }; });
    try { await axios.post(`${API}/${orgId}/config`, { id: setup.id, label: setup.label, api_base: setup.api_base, ext_org_id: setup.ext_org_id, forms }); setSetup(null); load(); toast.success('Saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const rotate = async (c) => {
    if (c.key_rotated_at && !window.confirm('Issue a new key? The current bookmark and schedule stop working until updated.')) return;
    try { const { data } = await axios.post(`${API}/${orgId}/config/${c.id}/rotate-key`); setNewKey({ ...data, cid: c.id }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const pause = async (c) => { await axios.post(`${API}/${orgId}/config/${c.id}/pause`, { paused: !c.paused }); load(); };
  const openDiffs = async (formId) => { const { data } = await axios.get(`${API}/${orgId}/diffs`, { params: { form_id: formId } }); setDiffs(data.rows || []); };

  if (!rows.length && !setup) return (
    <div className={`border rounded-lg p-3 text-xs ${card}`} data-testid="ext-sync-card">
      <span className={sub}>External registration sync (shadow mode): mirror registrations still taken on another platform.</span>{' '}
      <button className="underline text-amber-500" onClick={() => setSetup({ label: '', api_base: '', ext_org_id: '', forms_text: '' })} data-testid="ext-sync-setup">Set up</button>
    </div>
  );

  return (
    <div className={`border rounded-lg p-3 space-y-2 ${card}`} data-testid="ext-sync-card">
      <div className="flex items-center gap-2 text-sm font-semibold"><RefreshCw className="h-4 w-4 text-amber-500" /> External sync (shadow mode)
        <span className={`text-[11px] font-normal ${sub}`}>— read-only copies of registrations taken on the external platform; no OL charges or emails</span></div>
      {rows.map(c => (
        <div key={c.id} className="text-xs space-y-1" data-testid={`ext-sync-${c.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <b>{c.label}</b>
            {c.paused ? <span className="px-1.5 rounded bg-slate-600 text-white">paused</span> : <span className="px-1.5 rounded bg-emerald-700 text-white">active</span>}
            {c.last_run && (c.last_run.ok
              ? <span className="text-emerald-500 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />last sync {String(c.last_run.at).slice(0, 16).replace('T', ' ')} UTC ({c.last_run.trigger})</span>
              : <span className="text-red-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />last sync failed: {c.last_run.error === 'LOGIN_NEEDED' ? 'external login needed — log in there, then Sync now' : (c.last_run.error || 'see runs')}</span>)}
            {!c.last_run && <span className={sub}>no sync yet</span>}
            <button className="underline" onClick={() => pause(c)}>{c.paused ? <><Play className="h-3 w-3 inline" /> Resume</> : <><Pause className="h-3 w-3 inline" /> Pause</>}</button>
            <button className="underline" onClick={() => rotate(c)} data-testid="ext-sync-key"><KeyRound className="h-3 w-3 inline" /> {c.key_rotated_at ? 'New key / bookmark' : 'Create key / bookmark'}</button>
            <button className="underline" onClick={() => setSetup({ id: c.id, label: c.label, api_base: c.api_base, ext_org_id: c.ext_org_id, forms_text: (c.forms || []).map(f => `${f.ext_program_id}/${f.ext_form_id}`).join(' ') })}>Edit</button>
          </div>
          {(c.forms || []).map(f => (
            <div key={f.ext_form_id} className={`pl-3 ${sub}`}>
              External form {f.ext_form_id} → {f.ol_form_id ? <button className="underline text-amber-500" onClick={() => onOpenForm && onOpenForm(f.ol_form_id)}>OL shadow form</button> : 'created on first sync'}
              {f.counts && ` · registered ${f.counts.registered} · in progress ${f.counts.in_progress} · waitlist ${f.counts.waitlisted} · canceled ${f.counts.canceled}`}
              {f.ol_form_id && <> · <button className={`underline ${f.diffs ? 'text-red-500' : ''}`} onClick={() => openDiffs(f.ol_form_id)} data-testid={`ext-diffs-${f.ext_form_id}`}>{f.diffs || 0} OL differences</button></>}
            </div>
          ))}
          {(c.runs || []).length > 0 && (
            <details><summary className={sub}>Recent runs</summary>
              {(c.runs || []).map(r => <div key={r.id} className={sub}>{String(r.at).slice(0, 16).replace('T', ' ')} · {r.trigger} · {r.ok ? 'ok' : `failed: ${r.error || (r.forms || []).map(x => x.error).filter(Boolean).join('; ')}`} · {(r.forms || []).map(x => `${x.rows ?? 0} rows (+${x.new ?? 0} new, ${x.updated ?? 0} upd, ${x.canceled ?? 0} canceled)`).join(' | ')}</div>)}
            </details>
          )}
        </div>
      ))}

      {newKey && (
        <div className="border rounded p-2 text-xs space-y-1 border-amber-600" data-testid="ext-sync-newkey">
          <div className="flex justify-between"><b>New sync key — shown once</b><button onClick={() => setNewKey(null)}><X className="h-3 w-3" /></button></div>
          <div>1. Drag this button to your bookmarks bar: <Bookmark href={newKey.bookmarklet} /></div>
          <div>2. While logged in on the external platform, click it to sync now. Scheduled runs use the same key.</div>
          <div className="break-all"><span className={sub}>Key:</span> <code>{newKey.key}</code></div>
        </div>
      )}

      {setup && (
        <div className="border rounded p-2 text-xs space-y-1" data-testid="ext-sync-form">
          {[['label', 'Label'], ['api_base', 'External API base (https://…/v2)'], ['ext_org_id', 'External organization id'], ['forms_text', 'Forms: programId/formId, space separated']].map(([k, l]) => (
            <div key={k}><label className={sub}>{l}</label><input className="w-full rounded border px-1 bg-transparent border-slate-600" value={setup[k] || ''} onChange={e => setSetup(s => ({ ...s, [k]: e.target.value }))} /></div>
          ))}
          <div className="flex gap-2"><button className="px-2 py-0.5 rounded bg-amber-600 text-white" onClick={save}>Save</button><button onClick={() => setSetup(null)}>Cancel</button></div>
        </div>
      )}

      {diffs && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDiffs(null)} data-testid="ext-diffs">
          <div className="bg-white dark:bg-slate-900 border dark:border-slate-700 rounded-lg p-4 w-full max-w-2xl max-h-[80vh] overflow-y-auto text-sm" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between mb-2"><b>Where OL would differ from the external platform</b><button onClick={() => setDiffs(null)}><X className="h-4 w-4" /></button></div>
            {diffs.length === 0 && <p className={sub}>No differences.</p>}
            {diffs.map(r => (
              <div key={r.id} className="border-b dark:border-slate-700 py-1">
                <b>{r.staged_player?.first_name} {r.staged_player?.last_name}</b> <span className={sub}>({r.status}, external ${Number(r.external_payment?.total || 0).toFixed(2)})</span>
                <ul className="list-disc ml-5">{(r.shadow_diff || []).map((d, i) => <li key={i}>{d}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
